import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { confirmChannel } from './feedbackStore';

// Renders the dialogs asked for with confirmDialog(), one at a time.
// Cancel is a quiet text button before the confirm verb; the confirm button
// is red only for destructive actions, and then focus starts on Cancel.
export default function ConfirmHost() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const queueRef = useRef([]);
  // Kept in step with `current` at once (not after a render), so two
  // requests in the same tick queue up instead of one replacing the other
  // and leaving its caller waiting for ever.
  const currentRef = useRef(null);

  useEffect(
    () =>
      confirmChannel.subscribe((req) => {
        if (currentRef.current) {
          queueRef.current.push(req);
          return;
        }
        currentRef.current = req;
        setCurrent(req);
        setOpen(true);
      }),
    []
  );

  const finish = (answer) => {
    const req = currentRef.current;
    if (!req || !open) return;
    setOpen(false);
    try {
      req.resolve(answer);
    } catch (e) {
      // a caller's handler failing must not leave the dialog stuck
    }
  };

  const handleExited = () => {
    const next = queueRef.current.shift() || null;
    currentRef.current = next;
    setCurrent(next);
    if (next) setOpen(true);
  };

  if (!current) return null;
  const oneButton = current.cancelLabel === null;
  const destructive = current.destructive === true;
  const confirmLabel = current.confirmLabel || (oneButton ? t('ok') : t('confirm'));
  const cancelLabel = current.cancelLabel || t('cancel');
  const titleId = 'dd-confirm-title';
  const bodyId = 'dd-confirm-body';

  return (
    <Dialog
      open={open}
      onClose={() => finish(false)}
      TransitionProps={{ onExited: handleExited }}
      aria-labelledby={titleId}
      aria-describedby={current.body ? bodyId : undefined}
      maxWidth="xs"
      fullWidth
    >
      {current.title ? <DialogTitle id={titleId}>{current.title}</DialogTitle> : null}
      {current.body ? (
        <DialogContent>
          {typeof current.body === 'string' ? (
            <Typography id={bodyId} variant="body1" color="text.secondary">
              {current.body}
            </Typography>
          ) : (
            <div id={bodyId}>{current.body}</div>
          )}
        </DialogContent>
      ) : null}
      <DialogActions>
        {oneButton ? null : (
          <Button variant="text" color="neutral" onClick={() => finish(false)} autoFocus={destructive}>
            {cancelLabel}
          </Button>
        )}
        <Button
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={() => finish(true)}
          autoFocus={!destructive}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
